import React, { useState, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, Download, Smartphone, Image, CheckCircle2, Apple, Pipette, Settings, X } from 'lucide-react'
import { RiAndroidLine } from 'react-icons/ri'
import { isColorDark } from '../../utils/functions'
import bannerLogo from '../../assets/images/banner-black-min.png'
import brandLogo from '../../assets/images/brand-logo.png'
import iphoneMockup from '../../assets/images/iphone_mockup.png'
import pixelMockup from '../../assets/images/pixel_phone_mockup.png'
import dancingKitty from '../../assets/images/dancing-kitty-white-min.gif'
import GoogleAd from '../../components/common/GoogleAd'
import { trackImageUpload, trackDownload, trackColorChange, trackDonationClick, trackIconGeneration } from '../../components/common/GoogleAnalytics'

const MobileIconGenerator = ({
  uploadedImage,
  backgroundColor,
  setBackgroundColor,
  iconName,
  setIconName,
  padding,
  setPadding,
  platforms,
  setPlatforms,
  outputFormat,
  setOutputFormat,
  onImageUpload,
  handleGenerate,
  isGenerating,
  showToast,
  setShowToast,
  colorInputRef,
  showBadge,
  setShowBadge,
  badgeText,
  setBadgeText,
  badgeColor,
  setBadgeColor,
  monochromeImage,
  setMonochromeImage,
  onMonochromeImageUpload,
  showMonochrome,
  setShowMonochrome
}) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  // Auto-open customize panel when image is uploaded on mobile
  useEffect(() => {
    if (uploadedImage) {
      // Only auto-open if this is not the initial page load
      const isInitialLoad = !document.hasFocus()
      if (!isInitialLoad) {
        // Delay to allow preview animation to complete
        const timer = setTimeout(() => {
          setIsBottomSheetOpen(true)
        }, 800) // After preview animation completes
        
        return () => clearTimeout(timer)
      }
    }
  }, [uploadedImage])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onImageUpload(acceptedFiles[0])
      }
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  })

  const { getRootProps: getPreviewRootProps, getInputProps: getPreviewInputProps, isDragActive: isPreviewDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onImageUpload(acceptedFiles[0])
      }
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  })

  // Dropzone for monochrome images
  const { getRootProps: getMonochromeRootProps, getInputProps: getMonochromeInputProps, isDragActive: isMonochromeDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onMonochromeImageUpload(acceptedFiles[0])
      }
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  })

  const iconTypes = [
    { name: 'Round Icon', style: 'rounded-full', size: 100 },
    { name: 'Squircle', style: 'rounded-3xl', size: 100 },
    { name: 'Legacy Icon', style: 'rounded-lg', size: 100 }
  ]

  return (
    <div className="">
      {/* Mobile Header */}
      <div className="text-center mb-6">
        <img 
          src={bannerLogo} 
          alt="EasyAppIcon Banner" 
          className="mx-auto h-20 md:h-24 w-auto"
        />
      </div>

      {/* Mobile Banner and Title - Hidden when image uploaded */}
      {!uploadedImage && (
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 1, y: 0 }}
          animate={{ 
            opacity: uploadedImage ? 0 : 1, 
            height: uploadedImage ? 0 : 'auto'
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{ overflow: 'hidden' }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
            Generate your app icon easily.
          </h1>
          <p className="text-xl md:text-xl text-gray-600 font-medium">
            iOS + Adaptive Android Icon
          </p>
        </motion.div>
      )}

      {/* Mobile Preview Area */}
      <div className="min-h-[60vh] mb-12">
        {uploadedImage ? (
          <motion.div 
            className="bg-white rounded-xl shadow-soft border border-gray-200 p-4 h-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          >
            {/* Mobile Header */}
            <div className="flex items-center space-x-2 mb-4">
              <Smartphone className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
            </div>

            {/* Output Format Select and Download Button */}
            <div className="mb-6 space-y-4">
              {/* Output Format Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Android Output Format
                </label>
                <div className="flex bg-gray-100 rounded-xl p-1 w-full">
                  <button
                    onClick={() => setOutputFormat('png')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      outputFormat === 'png'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <div className="text-center">
                      <div className="font-semibold">PNG</div>
                      <div className="text-xs opacity-75 mt-0.5">Lossless quality</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setOutputFormat('webp')}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      outputFormat === 'webp'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <div className="text-center">
                      <div className="font-semibold">WebP</div>
                      <div className="text-xs opacity-75 mt-0.5">Smaller files</div>
                    </div>
                  </button>
                </div>
              </div>
              {/* Download Button */}
              <motion.button
                onClick={() => {
                  setShowDownloadModal(true)
                  handleGenerate(outputFormat)
                  
                  // Track icon generation
                  trackIconGeneration('mobile', backgroundColor !== '#ffffff', showBadge)
                  trackDownload('icon_package')
                }}
                disabled={isGenerating}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Download Icons</span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Mobile Icon Types */}
            <div className="mb-8">
              <div className="grid grid-cols-3 gap-4">
                {iconTypes.map((iconType) => (
                  <div key={iconType.name} className="text-center">
                    <div className="mb-3">
                      <div 
                        className={`mx-auto shadow-lg overflow-hidden ${iconType.style} relative`}
                        style={{ 
                          width: 80, 
                          height: 80,
                          backgroundColor: backgroundColor 
                        }}
                      >
                        <div 
                          className="w-full h-full flex items-center justify-center"
                          style={{ 
                            padding: padding > 0 ? `${padding / 2}%` : '0',
                            transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                          }}
                        >
                          <img 
                            src={uploadedImage} 
                            alt="Icon preview" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        {showBadge && badgeText && (
                          <div 
                            className="absolute bottom-0 left-0 right-0 text-white text-xs py-1 px-2 text-center"
                            style={{ backgroundColor: badgeColor }}
                          >
                            {badgeText}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 font-medium">{iconType.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Device Mockups */}
            <div className="grid grid-cols-2 gap-6">
              {/* iPhone Mockup */}
              <div className="text-center">
                <div className="relative mx-auto w-full max-w-[140px]">
                  <div className="relative w-full" style={{ aspectRatio: '200/400' }}>
                    <img 
                      src={iphoneMockup} 
                      alt="iPhone Mockup" 
                      className="w-full h-full object-contain filter drop-shadow-2xl"
                    />
                    {uploadedImage && (
                      <div 
                        className="absolute z-10 flex flex-col justify-center items-center"
                        style={{ 
                          top: '12%', 
                          left: '12%'
                        }}
                      >
                        <div 
                          className="overflow-hidden shadow-lg relative"
                          style={{ 
                            width: '32px', 
                            height: '32px',
                            backgroundColor: backgroundColor,
                            borderRadius: '7px',
                          }}
                        >
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ 
                              padding: padding > 0 ? `${padding / 2}%` : '0',
                              transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                            }}
                          >
                            <img 
                              src={uploadedImage} 
                              alt="App icon preview" 
                              className="w-full h-full object-contain"
                            />
                          </div>
                          {showBadge && badgeText && (
                            <div 
                              className="absolute bottom-0 left-0 right-0 text-white text-center"
                              style={{ 
                                backgroundColor: badgeColor,
                                fontSize: '3px',
                                paddingTop: '1px',
                                paddingBottom: '0px'
                              }}
                            >
                              {badgeText}
                            </div>
                          )}
                        </div>
                        <div className="mt-1 flex justify-center">
                          <p className="text-white font-medium text-center whitespace-nowrap select-none" style={{ 
                            fontSize: '6px', 
                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                          }}>
                            [App name]
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex flex-row items-center justify-center space-x-1">
                    <Apple className="w-3 h-3 text-gray-600" />
                    <p className="text-xs text-gray-600 font-medium">iPhone</p>
                  </div>
                </div>
              </div>

              {/* Android Mockup */}
              <div className="text-center">
                <div className="relative mx-auto w-full max-w-[140px]">
                  <div className="relative w-full" style={{ aspectRatio: '200/400' }}>
                    <img 
                      src={pixelMockup} 
                      alt="Pixel Phone Mockup" 
                      className="w-full h-full object-contain filter drop-shadow-2xl"
                    />
                    {uploadedImage && (
                      <div 
                        className="absolute z-10 flex flex-col justify-center items-center"
                        style={{ 
                          top: '12%', 
                          left: '13.5%'
                        }}
                      >
                        <div 
                          className="overflow-hidden shadow-lg relative"
                          style={{ 
                            width: '32px', 
                            height: '32px',
                            backgroundColor: backgroundColor,
                            borderRadius: '5px',
                          }}
                        >
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ 
                              padding: padding > 0 ? `${padding / 2}%` : '0',
                              transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                            }}
                          >
                            <img 
                              src={uploadedImage} 
                              alt="App icon preview" 
                              className="w-full h-full object-contain"
                            />
                          </div>
                          {showBadge && badgeText && (
                            <div 
                              className="absolute bottom-0 left-0 right-0 text-white text-center"
                              style={{ 
                                backgroundColor: badgeColor,
                                fontSize: '3px',
                                paddingTop: '1px',
                                paddingBottom: '0px'
                              }}
                            >
                              {badgeText}
                            </div>
                          )}
                        </div>
                        <div className="mt-1 flex justify-center">
                          <p className="text-white font-medium text-center whitespace-nowrap select-none" style={{ 
                            fontSize: '6px', 
                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                          }}>
                            [App name]
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex flex-row items-center justify-center space-x-1">
                    <RiAndroidLine className="w-3 h-3 text-gray-600" />
                    <p className="text-xs text-gray-600 font-medium">Android</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monochrome/Themed Icon Previews for Mobile */}
            {showMonochrome && monochromeImage && uploadedImage && (
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700">Android Themed Icons Preview</span>
                </div>
                
                <div className="grid grid-cols-4 gap-3">
                  {/* Blue Themed Icon */}
                  <div className="text-center">
                    <div 
                      className="mx-auto shadow-lg overflow-hidden rounded-lg relative"
                      style={{ 
                        width: '40px', 
                        height: '40px',
                        backgroundColor: '#3b82f6',
                      }}
                    >
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                          padding: padding > 0 ? `${padding / 2}%` : '0',
                          transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                        }}
                      >
                        <img 
                          src={monochromeImage} 
                          alt="Blue themed icon" 
                          className="w-full h-full object-contain"
                          style={{ 
                            filter: 'brightness(0) invert(1)'
                          }}
                        />
                      </div>
                      {showBadge && badgeText && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 text-white text-center"
                          style={{ 
                            backgroundColor: badgeColor,
                            fontSize: '6px',
                            paddingTop: '1px',
                            paddingBottom: '0px'
                          }}
                        >
                          {badgeText}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Blue</p>
                  </div>

                  {/* Green Themed Icon */}
                  <div className="text-center">
                    <div 
                      className="mx-auto shadow-lg overflow-hidden rounded-lg relative"
                      style={{ 
                        width: '40px', 
                        height: '40px',
                        backgroundColor: '#10b981',
                      }}
                    >
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                          padding: padding > 0 ? `${padding / 2}%` : '0',
                          transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                        }}
                      >
                        <img 
                          src={monochromeImage} 
                          alt="Green themed icon" 
                          className="w-full h-full object-contain"
                          style={{ 
                            filter: 'brightness(0) invert(1)'
                          }}
                        />
                      </div>
                      {showBadge && badgeText && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 text-white text-center"
                          style={{ 
                            backgroundColor: badgeColor,
                            fontSize: '6px',
                            paddingTop: '1px',
                            paddingBottom: '0px'
                          }}
                        >
                          {badgeText}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Green</p>
                  </div>

                  {/* Purple Themed Icon */}
                  <div className="text-center">
                    <div 
                      className="mx-auto shadow-lg overflow-hidden rounded-lg relative"
                      style={{ 
                        width: '40px', 
                        height: '40px',
                        backgroundColor: '#8b5cf6',
                      }}
                    >
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                          padding: padding > 0 ? `${padding / 2}%` : '0',
                          transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                        }}
                      >
                        <img 
                          src={monochromeImage} 
                          alt="Purple themed icon" 
                          className="w-full h-full object-contain"
                          style={{ 
                            filter: 'brightness(0) invert(1)'
                          }}
                        />
                      </div>
                      {showBadge && badgeText && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 text-white text-center"
                          style={{ 
                            backgroundColor: badgeColor,
                            fontSize: '6px',
                            paddingTop: '1px',
                            paddingBottom: '0px'
                          }}
                        >
                          {badgeText}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Purple</p>
                  </div>

                  {/* Dark Themed Icon */}
                  <div className="text-center">
                    <div 
                      className="mx-auto shadow-lg overflow-hidden rounded-lg relative"
                      style={{ 
                        width: '40px', 
                        height: '40px',
                        backgroundColor: '#1f2937',
                      }}
                    >
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ 
                          padding: padding > 0 ? `${padding / 2}%` : '0',
                          transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                        }}
                      >
                        <img 
                          src={monochromeImage} 
                          alt="Dark themed icon" 
                          className="w-full h-full object-contain"
                          style={{ 
                            filter: 'brightness(0) invert(1)'
                          }}
                        />
                      </div>
                      {showBadge && badgeText && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 text-white text-center"
                          style={{ 
                            backgroundColor: badgeColor,
                            fontSize: '6px',
                            paddingTop: '1px',
                            paddingBottom: '0px'
                          }}
                        >
                          {badgeText}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Dark</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            {...getPreviewRootProps()}
            className={`
              rounded-xl border-2 border-dashed p-8 text-center h-full flex items-center justify-center cursor-pointer transition-all duration-300 shadow-sm
              ${isPreviewDragActive 
                ? 'border-primary-500 bg-primary-50 shadow-lg ring-2 ring-primary-200' 
                : 'border-primary-300 bg-gradient-to-br from-primary-25 via-blue-25 to-purple-25 hover:border-primary-500 hover:bg-gradient-to-br hover:from-primary-50 hover:via-blue-50 hover:to-purple-50 hover:shadow-md'
              }
            `}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <input {...getPreviewInputProps()} />
            <div className="max-w-xs mx-auto">
              <div className="flex space-x-4 mb-6 justify-center">
                {isPreviewDragActive ? (
                  <Upload className="w-14 h-14 text-primary-500 animate-bounce" />
                ) : (
                  <>
                    <Apple className="w-14 h-14 text-primary-400 hover:text-primary-500 transition-colors" />
                    <RiAndroidLine className="w-14 h-14 text-primary-400 hover:text-primary-500 transition-colors" />
                  </>
                )}
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${isPreviewDragActive ? 'text-primary-700' : 'text-gray-800'}`}>
                {isPreviewDragActive ? 'Drop your image here!' : 'Ready to Create?'}
              </h3>
              <p className={`text-base text-primary-500 font-medium ${isPreviewDragActive ? 'text-primary-600' : 'text-gray-600'}`}>
                {isPreviewDragActive 
                  ? 'Release to upload your foreground image'
                  : 'Tap to upload an image and see live previews for iOS and Android'
                }
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Mobile Bottom Sheet */}
      <motion.div
        className={`fixed inset-0 z-50 ${isBottomSheetOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: isBottomSheetOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={() => setIsBottomSheetOpen(false)}
        />
        
        {/* Bottom Sheet */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] overflow-hidden pb-5"
          initial={{ y: '100%' }}
          animate={{ y: isBottomSheetOpen ? 0 : '100%' }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* Bottom Sheet Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Customize Icon</h3>
            <button
              onClick={() => setIsBottomSheetOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Bottom Sheet Content */}
          <div className="p-4 space-y-6 overflow-y-auto max-h-[calc(80vh-80px)]">
            {/* Upload Area */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Foreground Image</label>
              <motion.div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-300 shadow-sm
                  ${isDragActive 
                    ? 'border-primary-500 bg-primary-50 shadow-md ring-2 ring-primary-200' 
                    : uploadedImage
                      ? 'border-green-400 bg-green-50/50 hover:border-green-500 hover:bg-green-50'
                      : 'border-primary-300 bg-gradient-to-br from-primary-25 to-blue-25 hover:border-primary-500 hover:bg-gradient-to-br hover:from-primary-50 hover:to-blue-50 hover:shadow-md'
                  }
                `}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <input {...getInputProps()} />
                {uploadedImage ? (
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-green-200">
                      <img src={uploadedImage} alt="Uploaded" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-green-600 font-medium text-sm">Tap to change</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                      <Image className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <span className="text-gray-800 font-semibold text-sm">Drop image here or tap to upload</span>
                      <br />
                      <span className="text-xs text-primary-600 font-medium">PNG, JPG, SVG up to 10MB</span>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Padding/Zoom */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Size: {padding > 0 ? `${padding}% padding` : `${Math.abs(padding)}% zoom`}
              </label>
              <input
                type="range"
                min="-100"
                max="100"
                value={padding}
                onChange={(e) => setPadding(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>Zoom</span>
                <span>Normal</span>
                <span>Padding</span>
              </div>
            </div>

            {/* Background Color */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Background Color
              </label>
              <div className="relative">
                <input
                  ref={colorInputRef}
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="w-full h-12 rounded-lg"
                />
                
                {/* Hex Color Display */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span 
                    className={`font-mono text-sm font-medium cursor-pointer pointer-events-auto select-none transition-all duration-200 hover:scale-110 ${isColorDark(backgroundColor) ? 'text-white' : 'text-gray-600'}`}
                    onClick={() => {
                      navigator.clipboard.writeText(backgroundColor).then(() => {
                        setShowToast(true)
                        setTimeout(() => setShowToast(false), 3000)
                      }).catch(err => {
                        console.error('Failed to copy color:', err)
                      })
                    }}
                    title="Click to copy hex color"
                  >
                    {backgroundColor.toUpperCase()}
                  </span>
                </div>
                
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <Pipette className={`w-4 h-4 ${isColorDark(backgroundColor) ? 'text-white' : 'text-gray-500'}`} />
                </div>
              </div>
            </div>

            {/* Android File Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Android File Name
              </label>
              <input
                type="text"
                value={iconName}
                onChange={(e) => setIconName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="ic_launcher"
              />
            </div>

            {/* Output Format */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Android Output Format
              </label>
              <div className="flex bg-gray-100 rounded-xl p-1 w-full">
                <button
                  onClick={() => setOutputFormat('png')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    outputFormat === 'png'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">PNG</div>
                    <div className="text-xs opacity-75 mt-0.5">Lossless quality</div>
                  </div>
                </button>
                <button
                  onClick={() => setOutputFormat('webp')}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    outputFormat === 'webp'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-semibold">WebP</div>
                    <div className="text-xs opacity-75 mt-0.5">Smaller files</div>
                  </div>
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                iOS icons will always be PNG format as required by Apple
              </div>
            </div>

            {/* Advanced Customization */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Advanced Customization</h4>
              
              {/* Badge Section */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showBadge}
                        onChange={(e) => setShowBadge(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-700">Show Badge</span>
                  </div>
                </div>
                
                {showBadge && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {/* Badge Text Input */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Badge Text
                      </label>
                      <input
                        type="text"
                        value={badgeText}
                        onChange={(e) => setBadgeText(e.target.value)}
                        placeholder="Enter badge text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      />
                    </div>

                    {/* Badge Color Picker */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Badge Color
                      </label>
                      <div className="relative">
                        <input
                          type="color"
                          value={badgeColor}
                          onChange={(e) => setBadgeColor(e.target.value)}
                          className="w-full h-12 rounded-lg cursor-pointer"
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span 
                            className={`font-mono text-sm font-medium cursor-pointer pointer-events-auto select-none transition-all duration-200 hover:scale-110 ${isColorDark(badgeColor) ? 'text-white' : 'text-gray-600'}`}
                            onClick={() => {
                              navigator.clipboard.writeText(badgeColor).then(() => {
                                setShowToast(true)
                                setTimeout(() => setShowToast(false), 3000)
                              }).catch(err => {
                                console.error('Failed to copy color:', err)
                              })
                            }}
                            title="Click to copy hex color"
                          >
                            {badgeColor.toUpperCase()}
                          </span>
                        </div>
                        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                          <Pipette className={`w-4 h-4 ${isColorDark(badgeColor) ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Monochrome Section */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showMonochrome}
                        onChange={(e) => setShowMonochrome(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                    <div className="flex flex-col space-y-1">
                      <span className="text-sm font-medium text-gray-700">Android Monochrome Icon</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Android 14+
                      </span>
                    </div>
                  </div>
                </div>
                
                {showMonochrome && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    <p className="text-sm text-gray-500">
                      Upload a monochrome version for themed Android icons. Should be white on transparent background.
                    </p>
                
                    <motion.div
                      {...getMonochromeRootProps()}
                      className={`
                        border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-300 shadow-sm
                        ${isMonochromeDragActive 
                          ? 'border-green-500 bg-green-50 shadow-md ring-2 ring-green-200' 
                          : monochromeImage
                            ? 'border-green-400 bg-green-50/50 hover:border-green-500 hover:bg-green-50'
                            : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-green-50 hover:shadow-md'
                        }
                      `}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <input {...getMonochromeInputProps()} />
                      {monochromeImage ? (
                        <div className="flex items-center justify-center space-x-3">
                          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-green-200 bg-black">
                            <img src={monochromeImage} alt="Monochrome" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex items-center space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-green-600 font-medium text-sm">Tap to change</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Upload className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-gray-700 font-medium text-sm mb-1">Upload monochrome</div>
                            <div className="text-xs text-gray-500">White on transparent PNG</div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Google Ad */}
            <div className="pt-4 border-t border-gray-200" style={{ minWidth: '300px', width: '100%' }}>
              <GoogleAd 
                slot="4339337826"
                style={{ 
                  display: 'block',
                  minHeight: '100px',
                  width: '100%'
                }}
              />
            </div>
          </div>
        </motion.div>
      </motion.div>

      
      {/* Mobile Control Button - Only show when image is uploaded */}
      <div className="fixed bottom-5 right-5 z-40">
        <button
          onClick={() => setIsBottomSheetOpen(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center space-x-2 shadow-lg min-w-max"
        >
          <Settings className="w-5 h-5" />
          <span>Customize</span>
        </button>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <motion.div
          className="fixed bottom-20 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50"
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <CheckCircle2 className="w-4 h-4" />
          <span className="font-medium">Copied to clipboard!</span>
        </motion.div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Modal Header with Close Button */}
            <div className="flex justify-between items-start p-4 pb-0">
              <div></div> {/* Empty div for spacing */}
              <button
                onClick={() => setShowDownloadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 flex-shrink-0"
                title="Close"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 pb-6">
              <div className="flex flex-col items-center text-center mb-4">
                {/* Dancing Kitty */}
                <div className="mb-4">
                  <img 
                    src={dancingKitty} 
                    alt="Dancing Kitty" 
                    className="w-32 h-32 object-contain"
                  />
                </div>
                
                {/* Text Content */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Thank you for using our services!
                  </h2>
                  <p className="text-gray-600 text-base">
                    Downloading your icons now.
                  </p>
                </div>
              </div>
            </div>

            {/* Help Request Text */}
            <div className="px-6 py-4 text-center border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                If you found this tool helpful, please consider supporting us! Your support helps us keep this service free for everyone. 🙏
              </p>
            </div>

            {/* Button Row */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col space-y-3">
              {/* Top Buttons - Brand, Donation */}
              <div className="flex justify-center space-x-3 items-center">
                {/* Donation Button */}
                <a 
                  href="https://www.buymeacoffee.com/dg43aLHr6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackDonationClick()}
                >
                  <img 
                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" 
                    alt="Buy Me A Coffee" 
                    className="h-10 w-auto hover:scale-105 transition-transform duration-200"
                  />
                </a>
                {/* Brand Logo Button */}
                <a
                  href="https://easyio.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group h-10 px-3 bg-black rounded-lg flex items-center justify-center transition-all duration-300"
                >
                  <img
                    src={brandLogo}
                    alt="EasyAppIcon"
                    className="h-6 w-auto"
                  />
                  <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:ml-2 transition-all duration-500 ease-out text-white font-semibold text-sm">
                    Built by EasyIO.dev
                  </span>
                </a>
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDownloadModal(false)
                    window.location.reload();
                  }}
                  className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200 border border-gray-300 rounded-lg"
                >
                  Start Over
                </button>
                <button
                  onClick={() => {
                    setShowDownloadModal(false)
                    window.location.href = '/support-us'
                  }}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Give us some help
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default MobileIconGenerator 